import { useEffect, useState } from 'react'
import { AlertCircle, FileWarning, Loader2 } from 'lucide-react'
import {
  type AttachmentPreview,
  fetchAttachmentPreview,
} from '@/api/attachments'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatByteSize } from './format'
import { isReady, type LocalAttachment, type LocalAttachmentReady } from './types'

/**
 * Rich preview dialog for non-PDF attachments — images, text snippets, and
 * "unsupported, here's why" states. PDFs already have a first-class
 * preview pane elsewhere in the app, so we just send the user there.
 *
 * Backed by `GET /dashboard/attachments/{uuid}/preview`. The fetch lives in
 * a dedicated `ReadyPreviewBody` child component that remounts (via `key`)
 * whenever the attachment UUID changes — that keeps the load/error state
 * lifecycle purely "mount → fetch → cleanup on unmount" with no
 * synchronous resets inside an effect.
 */
export type AttachmentPreviewFetcher = (
  uuid: string,
  init?: { signal?: AbortSignal },
) => Promise<AttachmentPreview>

export function AttachmentPreviewDialog({
  attachment,
  open,
  onOpenChange,
  /** Inject a fetcher in tests so we don't have to mock the API module. */
  fetcher = fetchAttachmentPreview,
}: {
  attachment: LocalAttachment | null
  open: boolean
  onOpenChange: (open: boolean) => void
  fetcher?: AttachmentPreviewFetcher
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl"
        data-testid="attachment-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="attachment-preview-title">
            {attachment?.filename ?? 'Preview'}
          </DialogTitle>
          <DialogDescription>
            {attachment ? (
              <>
                {attachment.mimeType} · {formatByteSize(attachment.byteSize)}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <PreviewBody attachment={attachment} fetcher={fetcher} />
      </DialogContent>
    </Dialog>
  )
}

function PreviewBody({
  attachment,
  fetcher,
}: {
  attachment: LocalAttachment | null
  fetcher: AttachmentPreviewFetcher
}) {
  if (!attachment) return null
  if (!isReady(attachment)) {
    return (
      <EmptyState
        icon={Loader2}
        spin
        title="Preview is not ready yet"
        message="This attachment is still uploading — try again once it finishes."
        testId="attachment-preview-not-ready"
      />
    )
  }
  // Remount the loader whenever the attachment uuid changes so the
  // fetch/lifecycle resets to a clean state without a synchronous setState
  // inside an effect.
  return (
    <ReadyPreviewBody
      key={attachment.server.uuid}
      attachment={attachment}
      fetcher={fetcher}
    />
  )
}

type PreviewState =
  | { status: 'loading' }
  | { status: 'ready'; preview: AttachmentPreview }
  | { status: 'error'; message: string }

function ReadyPreviewBody({
  attachment,
  fetcher,
}: {
  attachment: LocalAttachmentReady
  fetcher: AttachmentPreviewFetcher
}) {
  const [state, setState] = useState<PreviewState>({ status: 'loading' })

  useEffect(() => {
    const ctl = new AbortController()
    let cancelled = false
    fetcher(attachment.server.uuid, { signal: ctl.signal })
      .then((res) => {
        if (cancelled) return
        setState({ status: 'ready', preview: res })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message =
          err instanceof Error ? err.message : 'Could not load preview.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
      ctl.abort()
    }
  }, [attachment.server.uuid, fetcher])

  if (state.status === 'loading') {
    return (
      <EmptyState
        icon={Loader2}
        spin
        title="Loading preview…"
        message="Fetching the preview from the server."
        testId="attachment-preview-loading"
      />
    )
  }
  if (state.status === 'error') {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Could not load preview"
        message={state.message}
        testId="attachment-preview-error"
      />
    )
  }
  const { preview } = state
  if (preview.kind === 'image') {
    return <ImagePreview preview={preview} />
  }
  if (preview.kind === 'text') {
    return <TextPreview preview={preview} />
  }
  return <UnsupportedPreview preview={preview} />
}

function ImagePreview({
  preview,
}: {
  preview: Extract<AttachmentPreview, { kind: 'image' }>
}) {
  return (
    <div className="space-y-3" data-testid="attachment-preview-image">
      <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
        <img
          src={preview.url}
          alt={preview.filename}
          className="mx-auto max-h-[60vh] w-full object-contain"
        />
      </div>
      {preview.width && preview.height ? (
        <p className="text-xs text-muted-foreground">
          {preview.width} × {preview.height} px
        </p>
      ) : null}
    </div>
  )
}

function TextPreview({
  preview,
}: {
  preview: Extract<AttachmentPreview, { kind: 'text' }>
}) {
  return (
    <div className="space-y-2" data-testid="attachment-preview-text">
      <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {preview.snippet}
      </pre>
      <p className="text-xs text-muted-foreground">
        {preview.truncated
          ? `Showing the first ${preview.snippet.length.toLocaleString()} of ${preview.total_chars.toLocaleString()} characters. The full file is sent to the agent.`
          : 'Full contents shown — the agent receives the same text.'}
      </p>
    </div>
  )
}

function UnsupportedPreview({
  preview,
}: {
  preview: Extract<AttachmentPreview, { kind: 'unsupported' }>
}) {
  return (
    <EmptyState
      icon={FileWarning}
      title="No preview for this file type"
      message={
        preview.reason ||
        'We can attach this file as context, but cannot render a preview here. The agent will still see the file when you send.'
      }
      testId="attachment-preview-unsupported"
    />
  )
}

function EmptyState({
  icon: Icon,
  spin,
  title,
  message,
  testId,
}: {
  icon: typeof Loader2
  spin?: boolean
  title: string
  message: string
  testId: string
}) {
  return (
    <div
      className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 px-6 py-10 text-center"
      data-testid={testId}
    >
      <Icon
        className={spin ? 'size-6 animate-spin text-muted-foreground' : 'size-6 text-muted-foreground'}
        aria-hidden
      />
      <h4 className="text-sm font-medium text-foreground">{title}</h4>
      <p className="max-w-prose text-xs text-muted-foreground">{message}</p>
    </div>
  )
}
